import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthProvider';
import RankBadge from './RankBadge';

export default function KitchenBar({ rankData, onRankClick }) {
  const { t } = useTranslation();
  const {
    user, kitchens, activeKitchen, setActiveKitchen,
    invites, signOut, renameKitchen, inviteToKitchen, acceptInvite,
  } = useAuth();

  const [inviteEmail, setInviteEmail] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [inviteStatus, setInviteStatus] = useState('');
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');

  const handleInvite = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;
    setInviteStatus(t('kitchen.sendingInvite'));
    const ok = await inviteToKitchen(inviteEmail.trim());
    setInviteStatus(ok ? t('kitchen.inviteSent') : t('kitchen.inviteFailed'));
    if (ok) setInviteEmail('');
    setTimeout(() => setInviteStatus(''), 3000);
  };

  const handleRename = async (e) => {
    e.preventDefault();
    if (!editName.trim() || !activeKitchen) {
      setEditing(false);
      return;
    }
    try {
      await renameKitchen(activeKitchen.id, editName.trim());
    } catch (err) {
      console.error('Failed to rename kitchen:', err);
    }
    setEditing(false);
  };

  const isOwner = activeKitchen && activeKitchen.role === 'owner';

  return (
    <div className="kitchen-bar">
      <div className="kitchen-bar-left">
        {kitchens.length > 1 ? (
          <select
            className="kitchen-select"
            value={activeKitchen?.id || ''}
            onChange={(e) => {
              const k = kitchens.find(k => k.id === e.target.value);
              if (k) setActiveKitchen(k);
            }}
          >
            {kitchens.map(k => (
              <option key={k.id} value={k.id}>{k.name}</option>
            ))}
          </select>
        ) : (
          editing ? (
            <form onSubmit={handleRename} className="kitchen-rename-form">
              <input
                autoFocus
                className="kitchen-rename-input"
                value={editName}
                onChange={e => setEditName(e.target.value)}
                onBlur={() => setEditing(false)}
                onKeyDown={e => e.key === 'Escape' && setEditing(false)}
              />
            </form>
          ) : (
            <span
              className={`kitchen-name${isOwner ? ' kitchen-name-editable' : ''}`}
              onClick={() => {
                if (!isOwner) return;
                setEditName(activeKitchen?.name || '');
                setEditing(true);
              }}
              title={isOwner ? t('kitchen.clickToRename') : ''}
            >
              {activeKitchen?.name || t('kitchen.defaultName')}
            </span>
          )
        )}
      </div>

      <RankBadge rankData={rankData} onClick={onRankClick} />

      <div className="kitchen-bar-right">
        {invites.length > 0 && (
          <div className="kitchen-invites">
            {invites.map(inv => (
              <span key={inv.id} className="kitchen-invite-badge">
                {inv.kitchens?.name}
                <button className="invite-accept-btn" onClick={() => acceptInvite(inv.id)}>{t('kitchen.join')}</button>
              </span>
            ))}
          </div>
        )}

        {isOwner && (
          <div className="kitchen-invite-form">
            {showInvite ? (
              <form onSubmit={handleInvite} className="invite-form-inline">
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={e => setInviteEmail(e.target.value)}
                  placeholder={t('kitchen.emailPlaceholder')}
                  className="invite-email-input"
                />
                <button type="submit" className="kitchen-bar-btn">{t('kitchen.invite')}</button>
                <button type="button" className="kitchen-bar-btn-muted" onClick={() => setShowInvite(false)}>{t('kitchen.close')}</button>
                {inviteStatus && <span className="invite-status">{inviteStatus}</span>}
              </form>
            ) : (
              <button className="kitchen-bar-btn" onClick={() => setShowInvite(true)}>{t('kitchen.inviteToKitchen')}</button>
            )}
          </div>
        )}

        <span className="kitchen-user-email">{user?.email}</span>
        <button className="kitchen-bar-btn-muted signout-btn" onClick={signOut}>
          <span className="signout-full">{t('auth.signOut')}</span>
          <span className="signout-compact">{t('auth.signOutCompact')}</span>
        </button>
      </div>
    </div>
  );
}
